import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, Button, Table, Tag, Select, DatePicker, Input, Space, Empty, Alert, Skeleton } from 'antd';
import {
  ArrowLeftOutlined, SyncOutlined, CheckCircleOutlined, WarningOutlined,
  CloseCircleOutlined, ClockCircleOutlined, LoadingOutlined, FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  getEstado, getRitmo, getHistorico,
  computeFaseActual, transcurridoSegundos, porcentajeFase, formatDuracion,
  type BrevoSyncEstadoResponse, type BrevoSyncRitmoPunto,
  type BrevoSyncHistoricoItem, type HistoricoFilters, type BrevoSyncEjecucion,
} from '../api/brevoSyncApi';
import PhaseProgress from '../components/BrevoSync/PhaseProgress';
import StatCards from '../components/BrevoSync/StatCards';
import SistemasModal from '../components/BrevoSync/SistemasModal';
import MarcarFallidaModal from '../components/BrevoSync/MarcarFallidaModal';
import LogModal from '../components/BrevoSync/LogModal';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ═══════════════════════════════════════════════════════════════
// Banner de estado con acciones de Sistemas
// ═══════════════════════════════════════════════════════════════

type VisualState = 'idle' | 'initializing' | 'slow_load' | 'in_progress' | 'stuck' | 'completed_ok' | 'completed_errors' | 'failed';

function getVisualState(estado: BrevoSyncEstadoResponse | null, ritmo?: BrevoSyncRitmoPunto[]): VisualState {
  if (!estado) return 'idle';
  if (estado.running) {
    const r = estado.running;
    const elapsed = transcurridoSegundos(r);
    const fase = computeFaseActual(r);
    if (fase === 'loading') return elapsed >= 300 ? 'slow_load' : 'initializing';
    if (ritmo && ritmo.length > 0 && elapsed > 300) {
      const ahora = new Date(r.ahora).getTime();
      const ultimoActivo = ritmo.filter(p => p.contactos > 0).map(p => new Date(p.minuto).getTime()).reduce((a, b) => Math.max(a, b), 0);
      if (ultimoActivo > 0 && (ahora - ultimoActivo) / 60000 >= 3) return 'stuck';
    }
    return 'in_progress';
  }
  if (estado.ultimaFinalizada) {
    const uf = estado.ultimaFinalizada;
    if (uf.estado === 'error') return 'failed';
    if (uf.faseAErrores > 0 || uf.faseBErrores > 0) return 'completed_errors';
    return 'completed_ok';
  }
  return 'idle';
}

function formatRelativeTime(isoDate: string, ahora: string): string {
  const diff = (new Date(ahora).getTime() - new Date(isoDate).getTime()) / 1000;
  if (diff < 60) return 'hace unos segundos';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} horas`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function useLocalElapsed(r: BrevoSyncEjecucion | undefined): string {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!r) return;
    setTick(0);
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [r?.id, r?.ahora]);
  if (!r) return '';
  return formatDuracion(Math.max(0, transcurridoSegundos(r) + tick));
}

interface SistemasBannerProps {
  estado: BrevoSyncEstadoResponse | null;
  ritmo?: BrevoSyncRitmoPunto[];
  loading: boolean;
  onMarcarFallida: (id: number) => void;
  onVerLog: (id: number) => void;
}

const SistemasBanner: React.FC<SistemasBannerProps> = ({ estado, ritmo, loading, onMarcarFallida, onVerLog }) => {
  const liveElapsed = useLocalElapsed(estado?.running);

  if (loading && !estado) return <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 16 }} />;

  const vs = getVisualState(estado, ritmo);
  const r = estado?.running;
  const uf = estado?.ultimaFinalizada;

  const configs: Record<VisualState, { type: 'info' | 'success' | 'warning' | 'error'; icon: React.ReactNode; message: string; description: string }> = {
    idle: {
      type: 'success', icon: <CheckCircleOutlined />,
      message: uf ? `Última sincronización: ${formatRelativeTime(uf.fin!, uf.ahora)}` : 'Sin ejecuciones registradas',
      description: uf ? `${(uf.faseAExitos + uf.faseBExitos).toLocaleString('es')} contactos · ${formatDuracion(Math.floor((new Date(uf.fin!).getTime() - new Date(uf.inicio).getTime()) / 1000))} · ejecución ${new Date(uf.inicio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}` : '',
    },
    initializing: {
      type: 'info', icon: <LoadingOutlined />,
      message: 'Cargando datos del CRM',
      description: r ? `Esta fase tarda unos 3 minutos · transcurrido ${liveElapsed}` : '',
    },
    slow_load: {
      type: 'warning', icon: <WarningOutlined />,
      message: 'La carga inicial está tardando más de lo habitual',
      description: r ? `Transcurrido ${liveElapsed}` : '',
    },
    in_progress: {
      type: 'info', icon: <SyncOutlined spin />,
      message: r ? (() => {
        const fase = computeFaseActual(r);
        const pct = fase === 'A' ? porcentajeFase(r.faseATotal, r.faseAExitos, r.faseAErrores) : porcentajeFase(r.faseBTotal, r.faseBExitos, r.faseBErrores);
        return `Fase ${fase} · ${pct != null ? pct + '%' : 'calculando…'}`;
      })() : 'En curso',
      description: r ? `Iniciada a las ${new Date(r.inicio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} · transcurrido ${liveElapsed}` : '',
    },
    stuck: {
      type: 'warning', icon: <ClockCircleOutlined />,
      message: 'Sin progreso reciente',
      description: r ? `La sincronización lleva ${liveElapsed} en marcha` : '',
    },
    completed_ok: {
      type: 'success', icon: <CheckCircleOutlined />,
      message: 'Sincronización completada',
      description: uf ? `${(uf.faseAExitos + uf.faseBExitos).toLocaleString('es')} contactos · ${formatDuracion(Math.floor((new Date(uf.fin!).getTime() - new Date(uf.inicio).getTime()) / 1000))} · finalizada ${formatRelativeTime(uf.fin!, uf.ahora)}` : '',
    },
    completed_errors: {
      type: 'warning', icon: <WarningOutlined />,
      message: uf ? `Completada con ${uf.faseAErrores + uf.faseBErrores} errores` : 'Completada con errores',
      description: uf ? `${(uf.faseAExitos + uf.faseBExitos).toLocaleString('es')} contactos sincronizados · los errores se reintentarán en la próxima sincronización` : '',
    },
    failed: {
      type: 'error', icon: <CloseCircleOutlined />,
      message: 'La sincronización falló',
      description: uf?.errorDetalle ? uf.errorDetalle.split('\n')[0] : 'Error desconocido',
    },
  };

  const cfg = configs[vs];
  const isRunningState = ['initializing', 'slow_load', 'in_progress', 'stuck'].includes(vs);

  const actions = isRunningState && r ? (
    <Space style={{ marginTop: 8 }}>
      {vs === 'stuck' && (
        <Button size="small" danger onClick={() => onMarcarFallida(r.id)}>Marcar como fallida</Button>
      )}
      <Button size="small" onClick={() => onVerLog(r.id)}>Ver log</Button>
    </Space>
  ) : null;

  return (
    <Alert
      type={cfg.type} icon={cfg.icon} message={cfg.message}
      description={actions ? <div>{cfg.description}{actions}</div> : cfg.description}
      showIcon style={{ marginBottom: 16, borderRadius: 8 }}
    />
  );
};

// ═══════════════════════════════════════════════════════════════
// Histórico ampliado para Sistemas
// ═══════════════════════════════════════════════════════════════

interface SistemasHistoryProps {
  onVerLog: (id: number) => void;
}

const tipoLabels: Record<string, string> = {
  diario: 'Diario',
  inicial: 'Carga inicial',
  setup_atributos: 'Setup atrib.',
  seed_control: 'Seed control',
};

const SistemasHistory: React.FC<SistemasHistoryProps> = ({ onVerLog }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BrevoSyncHistoricoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [estadoFilter, setEstadoFilter] = useState<string | undefined>(undefined);
  const [tipoFilter, setTipoFilter] = useState<string | undefined>(undefined);
  const [searchId, setSearchId] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(
    [dayjs().subtract(7, 'day'), dayjs()]
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const filters: HistoricoFilters = {
        page, pageSize,
        tipo: tipoFilter,
        estado: estadoFilter,
        q: searchId || undefined,
        desde: dateRange?.[0]?.format('YYYY-MM-DD'),
        hasta: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const result = await getHistorico(filters);
      setData(result.ejecuciones);
      setTotal(result.total);
    } catch { /* silently fail */ } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [page, pageSize, estadoFilter, tipoFilter, searchId, dateRange]);

  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'ok': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error': return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
      case 'running': return <SyncOutlined spin style={{ color: '#1890ff' }} />;
      default: return null;
    }
  };

  const estadoTag = (estado: string, item: BrevoSyncHistoricoItem) => {
    if (estado === 'ok' && (item.faseAErrores > 0 || item.faseBErrores > 0)) {
      return <Tag color="warning" icon={<WarningOutlined />}>{item.faseAErrores + item.faseBErrores} errores</Tag>;
    }
    const map: Record<string, { color: string; label: string }> = {
      ok: { color: 'success', label: 'Correcta' },
      error: { color: 'error', label: 'Fallida' },
      running: { color: 'processing', label: 'En curso' },
    };
    const cfg = map[estado] || { color: 'default', label: estado };
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
  };

  const columns: ColumnsType<BrevoSyncHistoricoItem> = [
    {
      title: 'ID', dataIndex: 'id', key: 'id', width: 60,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>#{val}</Text>,
    },
    {
      title: 'Fecha', dataIndex: 'inicio', key: 'fecha', width: 150,
      render: (val: string) => new Date(val).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 110,
      render: (val: string) => <Tag>{tipoLabels[val] || val}</Tag>,
    },
    { title: '', key: 'icon', width: 30, render: (_, r) => estadoIcon(r.estado) },
    {
      title: 'Resultado', key: 'resultado', width: 180,
      render: (_, r) => {
        if (r.estado === 'running') return estadoTag(r.estado, r);
        const total = r.faseAExitos + r.faseBExitos;
        return <span>{total.toLocaleString('es')} contactos {estadoTag(r.estado, r)}</span>;
      },
    },
    {
      title: 'Errores', key: 'errores', width: 70,
      render: (_, r) => {
        const errores = r.faseAErrores + r.faseBErrores;
        return errores === 0 ? <Text type="secondary">0</Text> : <Text type="danger">{errores}</Text>;
      },
    },
    {
      title: 'Duración', dataIndex: 'duracionSegundos', key: 'duracion', width: 100,
      render: (val: number) => formatDuracion(val),
    },
    {
      title: 'Parámetros', dataIndex: 'parametros', key: 'parametros', width: 140, ellipsis: true,
      render: (val: string | undefined) => val ? <Text code style={{ fontSize: 11 }}>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Origen', dataIndex: 'origen', key: 'origen', width: 90,
      render: (val: string) => <Text type="secondary" style={{ textTransform: 'capitalize' }}>{val}</Text>,
    },
  ];

  const expandedRowRender = (record: BrevoSyncHistoricoItem) => (
    <div style={{ padding: '8px 0' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text><strong>Inicio:</strong> {new Date(record.inicio).toLocaleString('es-ES')}</Text>
        {record.fin && <Text><strong>Fin:</strong> {new Date(record.fin).toLocaleString('es-ES')}</Text>}
        <Text><strong>Fase A:</strong> {record.faseAExitos} OK, {record.faseAErrores} errores{record.faseATotal ? ` de ${record.faseATotal.toLocaleString('es')}` : ''}</Text>
        <Text><strong>Fase B:</strong> {record.faseBExitos} OK, {record.faseBErrores} errores{record.faseBTotal ? ` de ${record.faseBTotal.toLocaleString('es')}` : ''}</Text>
        <Text><strong>Duplicados fusionados:</strong> {record.duplicadosFusionados}</Text>
        {record.parametros && <Text><strong>Parámetros:</strong> {record.parametros}</Text>}
        {record.errorDetalle && (
          <div style={{ background: '#fff2f0', padding: '8px 12px', borderRadius: 4, marginTop: 4 }}>
            <Text type="danger" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{record.errorDetalle}</Text>
          </div>
        )}
        <Button size="small" icon={<FileTextOutlined />} onClick={() => onVerLog(record.id)} style={{ marginTop: 8 }}>Ver log</Button>
      </Space>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14 }}>Histórico de sincronizaciones</Text>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <RangePicker value={dateRange} onChange={(dates) => { setDateRange(dates); setPage(1); }} format="DD/MM/YYYY" placeholder={['Desde', 'Hasta']} size="small" />
        <Select value={estadoFilter} onChange={(val) => { setEstadoFilter(val); setPage(1); }} placeholder="Estado" allowClear size="small" style={{ width: 140 }}
          options={[{ label: 'Correctas', value: 'ok' }, { label: 'Fallidas', value: 'error' }, { label: 'En curso', value: 'running' }]} />
        <Select value={tipoFilter} onChange={(val) => { setTipoFilter(val); setPage(1); }} placeholder="Tipo" allowClear size="small" style={{ width: 150 }}
          options={[{ label: 'Diario', value: 'diario' }, { label: 'Carga inicial', value: 'inicial' }, { label: 'Setup atributos', value: 'setup_atributos' }, { label: 'Seed control', value: 'seed_control' }]} />
        <Input value={searchId} onChange={e => { setSearchId(e.target.value); setPage(1); }} placeholder="Buscar por ID" allowClear size="small" style={{ width: 120 }} />
      </div>
      <Table
        dataSource={data.map(d => ({ ...d, key: d.id }))} columns={columns} loading={loading}
        expandable={{ expandedRowRender }}
        pagination={{
          current: page, pageSize, total,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showSizeChanger: true, pageSizeOptions: ['10', '30', '50', '100'],
          showTotal: (t) => `${t} ejecuciones`, size: 'small',
        }}
        size="small" locale={{ emptyText: <Empty description="Sin ejecuciones registradas" /> }}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Página principal de Sistemas
// ═══════════════════════════════════════════════════════════════

const BrevoSyncSistemasPage: React.FC = () => {
  const [estado, setEstado] = useState<BrevoSyncEstadoResponse | null>(null);
  const [ritmo, setRitmo] = useState<BrevoSyncRitmoPunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [sistemasVisible, setSistemasVisible] = useState(false);
  const [marcarFallidaVisible, setMarcarFallidaVisible] = useState(false);
  const [marcarFallidaId, setMarcarFallidaId] = useState<number | null>(null);
  const [logVisible, setLogVisible] = useState(false);
  const [logId, setLogId] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const isRunning = !!estado?.running;
  const isLoadingPhase = isRunning && estado?.running ? computeFaseActual(estado.running) === 'loading' : false;

  const fetchEstado = useCallback(async () => {
    try {
      const data = await getEstado();
      setEstado(data);
      setLoading(false);
      if (data.running) {
        try {
          const ritmoData = await getRitmo(data.running.id, 'A');
          setRitmo(ritmoData.puntos);
        } catch { /* silently fail */ }
      }
    } catch { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchEstado();
    const setupInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const delay = isRunning ? (isLoadingPhase ? 20000 : 5000) : 60000;
      intervalRef.current = setInterval(fetchEstado, delay);
    };
    setupInterval();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRunning, isLoadingPhase, fetchEstado]);

  useEffect(() => {
    const handleVisibility = () => { if (document.visibilityState === 'visible') fetchEstado(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchEstado]);

  const handleLaunched = () => {
    let attempts = 0;
    const poll = () => { fetchEstado(); attempts++; if (attempts < 15) setTimeout(poll, 2000); };
    setTimeout(poll, 1000);
  };

  const openLog = (id: number) => { setLogId(id); setLogVisible(true); };
  const openMarcarFallida = (id: number) => { setMarcarFallidaId(id); setMarcarFallidaVisible(true); };

  return (
    <div style={{ padding: '0 8px' }}>
      <div style={{ background: '#fff', padding: '16px 24px', borderRadius: 8 }}>
        {/* Cabecera */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/brevo-sync')} size="small" />
            <Title level={4} style={{ margin: 0 }}>Sincronización Brevo — Sistemas</Title>
          </div>
          <Button type="primary" icon={<SyncOutlined />} onClick={() => setSistemasVisible(true)}>
            Lanzar sincronización
          </Button>
        </div>

        {/* Banner con acciones */}
        <SistemasBanner
          estado={estado} ritmo={ritmo} loading={loading}
          onMarcarFallida={openMarcarFallida} onVerLog={openLog}
        />

        {/* Progreso por fases */}
        {estado?.running && <PhaseProgress ejecucion={estado.running} />}

        {/* Cards de métricas */}
        <StatCards ejecucion={estado?.running} ultimaFinalizada={estado?.ultimaFinalizada} ritmo={ritmo} />

        {/* Histórico ampliado */}
        <SistemasHistory onVerLog={openLog} />

        {/* Modales */}
        <SistemasModal visible={sistemasVisible} hasRunning={isRunning} onClose={() => setSistemasVisible(false)} onLaunched={handleLaunched} />
        <MarcarFallidaModal visible={marcarFallidaVisible} executionId={marcarFallidaId} onClose={() => setMarcarFallidaVisible(false)} onMarked={() => fetchEstado()} />
        <LogModal visible={logVisible} executionId={logId} onClose={() => setLogVisible(false)} />
      </div>
    </div>
  );
};

export default BrevoSyncSistemasPage;
